import {
  cleanBarcode,
  convertQty,
  COUNTER_VOID_WINDOW_MINUTES,
  formatDecimal,
  parseDecimal,
  QTY_SCALE,
  type EntryDto,
  type EntryInput,
  type ItemDetailDto,
  type ItemTotalDto,
  type Paginated,
} from '@bnn/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { BarcodeBindForm } from '../components/BarcodeBindForm';
import { VoidDialog } from '../components/VoidDialog';
import { ItemPicker, UomOptions } from '../components/ItemPicker';
import { Alert, Badge, Button, Card, Input, Modal, Select, Spinner } from '../components/ui';
import { CameraScanner } from '../count/CameraScanner';
import { feedback } from '../count/feedback';
import { isLargeQty } from '../count/guards';
import { getItem, lookupBarcode, MASTER_STALE_MS, masterStatus, onMasterChange, rememberBarcode, syncMaster, type MasterStatus } from '../count/master';
import { useCachedSession } from '../count/sessionCache';
import { api } from '../lib/api';
import { useOnline } from '../lib/online';
import { hm, qty as fmtQty, STATUS_LABEL } from '../lib/format';
import { errMsg, useApi } from '../lib/useApi';
import { useOutbox, type EntryOp } from '../outbox';

/** Same barcode read again within this window is a double-read from the scanner, not a second count. */
const DUPLICATE_SCAN_MS = 450;

interface Current {
  item: ItemDetailDto;
  uom: string;
  barcode: string | null;
  method: 'SCAN' | 'SEARCH';
  /** scanned while offline / without binding (Requirements §5.4 option ก) */
  unbound: string | null;
  /** entry this one replaces (re-count after void) */
  replaces: string | null;
}

type Dialog =
  | { type: 'search'; unbound?: string }
  | { type: 'bind'; barcode?: string; item?: ItemDetailDto; uom?: string }
  | { type: 'camera' }
  | { type: 'offline'; barcode: string };

type Msg = { tone: 'ok' | 'warn' | 'bad'; text: string } | null;

interface RecentRow {
  id: string;
  at: string;
  item_code: string;
  item_name: string;
  qty: string;
  uom: string;
  barcode: string | null;
  unbound: string | null;
  method: 'SCAN' | 'SEARCH';
  status: 'PENDING' | 'SYNCED' | 'REJECTED' | 'CANCELLED' | 'VOID' | 'VOIDING';
  note?: string;
}

const sumQty = (xs: (string | null | undefined)[]) =>
  formatDecimal(
    xs.reduce((s, x) => s + (x ? (parseDecimal(x, QTY_SCALE) ?? 0n) : 0n), 0n),
    QTY_SCALE,
  );

export default function CountPage() {
  const { id = '' } = useParams();
  const session = useCachedSession(id);
  const online = useOnline();
  const box = useOutbox();
  const [master, setMaster] = useState<MasterStatus | null>(null);
  const [masterError, setMasterError] = useState('');
  const [cur, setCur] = useState<Current | null>(null);
  const [qty, setQty] = useState('');
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [total, setTotal] = useState<ItemTotalDto | null>(null);
  const recent = useApi<Paginated<EntryDto>>(`/sessions/${id}/entries?mine=true&page_size=30`);
  const scanRef = useRef<HTMLInputElement>(null);
  const qtyRef = useRef<HTMLInputElement>(null);
  const lastScan = useRef({ v: '', t: 0 });

  const { can } = useAuth();
  const voidAny = can('entries.voidAny');
  const [voidTarget, setVoidTarget] = useState<{ row: RecentRow; mode: 'void' | 'fix' } | null>(null);
  const localOps = box.ops().filter((o): o is EntryOp => o.kind === 'ENTRY' && o.session_id === id);
  // Any op (entry or void) confirmed by the server -> refresh totals and the recent list.
  const syncedCount = box.ops().filter((o) => o.session_id === id && o.status === 'SYNCED').length;
  const pending = box.pendingCount(id);

  const curRef = useRef<Current | null>(null);
  curRef.current = cur;
  // Never pull focus away from the quantity field while an item is selected, or from an open dialog.
  const focusScan = useCallback(
    () =>
      setTimeout(() => {
        if (!curRef.current && !document.querySelector('dialog[open]')) scanRef.current?.focus();
      }, 30),
    [],
  );

  // Keep the scan field focused for HID scanners whenever nothing else needs the keyboard.
  useEffect(() => {
    const onFocusOut = () =>
      setTimeout(() => {
        const a = document.activeElement;
        if (dialog || cur) return;
        if (!a || a === document.body || !(a instanceof HTMLInputElement || a instanceof HTMLSelectElement || a instanceof HTMLTextAreaElement)) {
          scanRef.current?.focus();
        }
      }, 150);
    document.addEventListener('focusout', onFocusOut);
    return () => document.removeEventListener('focusout', onFocusOut);
  }, [dialog, cur]);

  // Master cache: show its age, and pull changes whenever the page opens online.
  useEffect(() => {
    const refresh = () => void masterStatus().then(setMaster);
    refresh();
    const off = onMasterChange(refresh);
    if (online) {
      syncMaster()
        .then((s) => {
          setMaster(s);
          setMasterError('');
        })
        .catch((e) => setMasterError(errMsg(e)));
    }
    return () => {
      off();
    };
  }, [online]);

  // Server running total for the selected item; refreshed whenever more of our entries are synced.
  const itemCode = cur?.item.item_code;
  useEffect(() => {
    if (!itemCode || !online) return setTotal(null);
    let alive = true;
    api
      .get<ItemTotalDto>(`/sessions/${id}/items/${encodeURIComponent(itemCode)}/total`)
      .then((t) => alive && setTotal(t))
      .catch(() => alive && setTotal(null));
    return () => {
      alive = false;
    };
  }, [id, itemCode, syncedCount, online]);

  const recentReload = recent.reload;
  useEffect(() => {
    recentReload();
  }, [syncedCount, recentReload]);

  const select = (c: Current) => {
    curRef.current = c;
    setCur(c);
    setQty('');
    setTimeout(() => qtyRef.current?.focus(), 30);
  };

  const clear = () => {
    curRef.current = null;
    setCur(null);
    setQty('');
    focusScan();
  };

  async function handleScan(raw: string) {
    const code = cleanBarcode(raw);
    if (!code) return;
    const now = Date.now();
    if (code === lastScan.current.v && now - lastScan.current.t < DUPLICATE_SCAN_MS) return;
    lastScan.current = { v: code, t: now };
    if (cur && qty.trim()) {
      feedback(false);
      setMsg({ tone: 'warn', text: 'มีจำนวนที่ยังไม่บันทึก — กด บันทึก หรือ ยกเลิก ก่อนสแกนรายการถัดไป' });
      qtyRef.current?.focus();
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await lookupBarcode(code);
      if (r.kind === 'found') {
        const d = r.data;
        if (!d.is_active) {
          feedback(false);
          return setMsg({ tone: 'bad', text: `Barcode ${code} ถูกปิดใช้งาน (ผูกกับ ${d.item.item_code} ${d.item.item_name}) — บันทึกนับไม่ได้` });
        }
        if (!d.item.countable) {
          feedback(false);
          return setMsg({ tone: 'bad', text: `${d.item.item_code} ${d.item.item_name} ไม่ใช่สินค้าคงคลัง — บันทึกนับไม่ได้` });
        }
        const item = await getItem(d.item.item_code);
        if (!item) throw new Error('ไม่พบสินค้า');
        feedback(true);
        return select({ item, uom: d.uom_code, barcode: code, method: 'SCAN', unbound: null, replaces: null });
      }
      if (r.kind === 'offline') {
        feedback(false);
        return setDialog({ type: 'offline', barcode: code });
      }
      // Not a known barcode: maybe an item code typed by hand.
      const item = await getItem(code).catch(() => null);
      if (item?.countable) {
        feedback(true);
        return select({ item, uom: item.inventory_uom, barcode: null, method: 'SEARCH', unbound: null, replaces: null });
      }
      if (item) {
        feedback(false);
        return setMsg({ tone: 'bad', text: `${item.item_code} ${item.item_name} นับไม่ได้ (ไม่ใช่สินค้าคงคลัง)` });
      }
      feedback(false);
      setDialog({ type: 'bind', barcode: code });
    } catch (e) {
      feedback(false);
      setMsg({ tone: 'bad', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!cur) return;
    const q = parseDecimal(qty, QTY_SCALE);
    if (q == null) {
      feedback(false);
      setMsg({ tone: 'bad', text: 'จำนวนไม่ถูกต้อง (ตัวเลข ≥ 0 ทศนิยมไม่เกิน 4 ตำแหน่ง)' });
      return qtyRef.current?.focus();
    }
    if (q === 0n && !confirm('บันทึกจำนวน 0 ใช่หรือไม่?')) return qtyRef.current?.focus();
    if (isLargeQty(q, cur.uom) && !confirm(`จำนวน ${fmtQty(formatDecimal(q, QTY_SCALE))} ${cur.uom} สูงผิดปกติ ยืนยันบันทึก?`)) {
      return qtyRef.current?.focus();
    }
    const qtyStr = formatDecimal(q, QTY_SCALE);
    const u = cur.item.uoms.find((x) => x.uom_code === cur.uom);
    const converted = u ? convertQty(qtyStr, { num: BigInt(u.factor_num), den: BigInt(u.factor_den) }) : null;
    const entry: EntryInput = {
      id: crypto.randomUUID(),
      session_id: id,
      item_code: cur.item.item_code,
      uom_code: cur.uom,
      barcode: cur.barcode,
      unbound_barcode: cur.unbound,
      entry_method: cur.method,
      original_qty: qtyStr,
      conversion_factor: u ? u.factor : null,
      converted_qty: converted,
      inventory_uom: cur.item.inventory_uom,
      conversion_status: u ? 'OK' : 'PENDING',
      replaces_entry_id: cur.replaces,
      item_inactive: !cur.item.is_active, // snapshot of the SAP status at counting time (v2.2)
      counted_at: new Date().toISOString(),
    };
    await box.addEntry(entry, cur.item.item_name);
    feedback(true);
    setMsg({
      tone: 'ok',
      text:
        `บันทึกแล้ว: ${cur.item.item_code} ${fmtQty(qtyStr)} ${cur.uom}` +
        (converted && cur.uom !== cur.item.inventory_uom ? ` = ${fmtQty(converted)} ${cur.item.inventory_uom}` : '') +
        (u ? '' : ' (รอแปลงหน่วย)'),
    });
    clear();
  }

  const onScanKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const v = e.currentTarget.value;
      e.currentTarget.value = '';
      void handleScan(v);
    }
  };

  const closeDialog = () => {
    setDialog(null);
    if (!cur) focusScan();
  };

  // Local (this device) entries not yet confirmed by the server, for the running total.
  const localUnsynced = useMemo(
    () => localOps.filter((o) => o.status === 'PENDING' && o.entry.item_code === itemCode && o.entry.conversion_status === 'OK'),
    [localOps, itemCode],
  );

  if (session.error) return <Alert>{session.error.message}</Alert>;
  if (!session.data) return <Spinner />;
  const s = session.data;

  if (s.status !== 'OPEN') {
    return (
      <Card>
        <p>
          รอบการนับ <b>{s.session_code}</b> อยู่ในสถานะ <b>{STATUS_LABEL[s.status]}</b> — บันทึกนับเพิ่มไม่ได้
        </p>
        <Link to={`/sessions/${s.id}`} className="mt-2 inline-block text-brand underline">
          ดูรอบการนับ
        </Link>
      </Card>
    );
  }

  const activeBarcodes = cur?.item.barcodes.filter((b) => b.is_active).length ?? 0;
  const pendingInv = sumQty(localUnsynced.map((o) => o.entry.converted_qty));
  const totalInv = total ? sumQty([total.total_inventory_qty, pendingInv]) : pendingInv;

  // Recent: this device's local ops first, then server entries not already shown.
  const localKeys = new Set(localOps.map((o) => o.key));
  const serverById = new Map((recent.data?.data ?? []).map((e) => [e.id, e]));
  const voiding = new Set(box.ops().filter((o) => o.kind === 'VOID' && o.status === 'PENDING').map((o) => (o.kind === 'VOID' ? o.entry_id : '')));
  const recentRows: RecentRow[] = [
    ...[...localOps].reverse().map(
      (o): RecentRow => ({
        id: o.key,
        at: o.entry.counted_at,
        item_code: o.entry.item_code,
        item_name: o.item_name,
        qty: o.entry.original_qty,
        uom: o.entry.uom_code,
        barcode: o.entry.barcode ?? null,
        unbound: o.entry.unbound_barcode ?? null,
        method: o.entry.entry_method,
        status: voiding.has(o.key) ? 'VOIDING' : serverById.get(o.key)?.is_void ? 'VOID' : o.status,
        note: o.local_void_reason ?? serverById.get(o.key)?.void_reason ?? o.error,
      }),
    ),
    ...(recent.data?.data ?? [])
      .filter((e) => !localKeys.has(e.id))
      .map(
        (e): RecentRow => ({
          id: e.id,
          at: e.counted_at,
          item_code: e.item_code,
          item_name: e.item_name,
          qty: e.original_qty,
          uom: e.uom_code,
          barcode: e.barcode,
          unbound: e.unbound_barcode,
          method: e.entry_method,
          status: voiding.has(e.id) ? 'VOIDING' : e.is_void ? 'VOID' : 'SYNCED',
          note: e.void_reason ?? undefined,
        }),
      ),
  ].slice(0, 30);
  const canVoid = (r: RecentRow) =>
    (r.status === 'PENDING' || r.status === 'SYNCED') && (voidAny || Date.now() - new Date(r.at).getTime() <= COUNTER_VOID_WINDOW_MINUTES * 60_000);

  const doVoid = async (reason: string) => {
    if (!voidTarget) return;
    const r = voidTarget.row;
    await box.addVoid(r.id, id, reason);
    setVoidTarget(null);
    if (voidTarget.mode === 'fix') {
      const item = await getItem(r.item_code);
      if (item) {
        setMsg({ tone: 'warn', text: `ยกเลิกรายการเดิม ${fmtQty(r.qty)} ${r.uom} แล้ว — กรอกจำนวนที่ถูกต้อง` });
        return select({ item, uom: r.uom, barcode: r.barcode, method: r.method, unbound: r.unbound, replaces: r.id });
      }
    }
    setMsg({ tone: 'ok', text: `ยกเลิกรายการ ${r.item_code} ${fmtQty(r.qty)} ${r.uom} แล้ว` });
    focusScan();
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={online && box.online ? 'ok' : 'bad'}>{online && box.online ? '● Online' : '● Offline'}</Badge>
        {pending > 0 && (
          <>
            <Badge tone="warn">รอ Sync {pending} รายการ</Badge>
            <Button size="sm" disabled={!online} onClick={() => void box.flush()}>
              Sync ตอนนี้
            </Button>
          </>
        )}
        <span className="ml-auto text-right">
          <Link to={`/sessions/${s.id}`} className="font-semibold text-brand hover:underline">
            {s.session_code}
          </Link>{' '}
          · <b>{s.warehouse_code}</b> {s.warehouse_name}
        </span>
      </div>
      {master && master.item_count === 0 && (
        <div className="mb-3">
          <Alert tone="warn">ยังไม่มีข้อมูลสินค้าในเครื่อง — ต้องเปิดหน้านี้ตอนมีเน็ตอย่างน้อย 1 ครั้งก่อนนับแบบออฟไลน์{masterError && ` (${masterError})`}</Alert>
        </div>
      )}
      {master && master.item_count > 0 && master.synced_at && Date.now() - Date.parse(master.synced_at) > MASTER_STALE_MS && (
        <div className="mb-3">
          <Alert tone="warn">ข้อมูลสินค้าในเครื่องเก่ากว่า 24 ชม. (อัปเดตล่าสุด {new Date(master.synced_at).toLocaleString('th-TH')}) — ต่อเน็ตเพื่ออัปเดต</Alert>
        </div>
      )}
      {session.offline && (
        <div className="mb-3">
          <Alert tone="info">ออฟไลน์ — นับต่อได้ รายการจะเก็บในเครื่องและส่งเองเมื่อกลับมาออนไลน์ (ห้ามล้างข้อมูล Browser)</Alert>
        </div>
      )}

      <Card>
        <div className="flex gap-2">
          <input
            ref={scanRef}
            onKeyDown={onScanKey}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="search"
            placeholder="สแกน / พิมพ์ Barcode หรือรหัสสินค้า"
            aria-label="Barcode"
            className="min-w-0 grow rounded-lg border border-[#c9ced8] bg-white px-3 py-3 text-lg tracking-wide focus:border-brand focus:outline-2 focus:outline-brand"
          />
          <Button size="lg" onClick={() => setDialog({ type: 'camera' })} aria-label="สแกนด้วยกล้อง" title="สแกนด้วยกล้อง">
            📷
          </Button>
          <Button size="lg" onClick={() => setDialog({ type: 'search' })} aria-label="ค้นหาสินค้า" title="ค้นหาสินค้า">
            🔍
          </Button>
        </div>
        {busy && (
          <div className="mt-2 text-sm text-muted">
            <Spinner className="size-4" /> กำลังค้นหา…
          </div>
        )}

        {cur && (
          <div className="mt-3 space-y-3">
            {!cur.item.is_active && (
              // v2.2: Inactive in SAP can still be counted (the status may be wrong) — make it obvious.
              <div role="status" className="rounded-lg border border-orange-300 bg-orange-100 px-3 py-2.5 text-sm font-medium text-orange-900">
                ⚠ สินค้า Inactive ใน SAP — ตรวจสอบกับทีม Cost
              </div>
            )}
            <div className="rounded-lg bg-chip px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <b>{cur.item.item_code}</b>
                <Badge>{cur.item.item_group ?? '-'}</Badge>
                {cur.barcode ? <Badge>BC {cur.barcode}</Badge> : <Badge>ค้นหา</Badge>}
                {cur.unbound && <Badge tone="warn">ไม่ผูก BC {cur.unbound}</Badge>}
                {cur.replaces && <Badge tone="warn">แก้ยอด</Badge>}
                {!cur.item.is_active && <Badge tone="warn">Inactive</Badge>}
              </div>
              <div className="text-lg leading-snug">{cur.item.item_name}</div>
              <div className="mt-1 text-[15px]">
                ยอดสะสม: <b className="text-lg">{fmtQty(totalInv)}</b> {cur.item.inventory_uom}
                <span className="text-xs text-muted">
                  {' '}
                  {total
                    ? `(ทุกเครื่องที่ Sync แล้ว${localUnsynced.length > 0 ? ` + เครื่องนี้รอส่ง ${fmtQty(pendingInv)}` : ''})`
                    : '(ออฟไลน์: เฉพาะรายการในเครื่องนี้ที่ยังไม่ส่ง)'}
                </span>
                {total?.pending_conversion && (
                  <>
                    {' '}
                    <Badge tone="warn">มีรายการรอแปลงหน่วย</Badge>
                  </>
                )}
              </div>
              {total && total.by_uom.length > 1 && (
                <div className="mt-0.5 text-xs text-muted">{total.by_uom.map((b) => `${fmtQty(b.original_qty)} ${b.uom_code}`).join(' + ')}</div>
              )}
            </div>

            {activeBarcodes === 0 ? (
              <Alert tone="warn">
                สินค้านี้ยังไม่มี Barcode{' '}
                {online ? (
                  <Button size="sm" className="ml-1" onClick={() => setDialog({ type: 'bind', item: cur.item, uom: cur.uom })}>
                    บันทึก Barcode
                  </Button>
                ) : (
                  <span className="text-xs">— บันทึก Barcode ต้องเชื่อมต่อเน็ต (นับด้วยการค้นหาได้ตามปกติ)</span>
                )}
              </Alert>
            ) : (
              online &&
              cur.method === 'SEARCH' && (
                <button className="text-sm text-brand underline" onClick={() => setDialog({ type: 'bind', item: cur.item, uom: cur.uom })}>
                  + เพิ่ม Barcode ให้สินค้านี้
                </button>
              )
            )}

            <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-2">
              <label className="block">
                <span className="mb-1 block text-sm text-muted">หน่วยที่นับ{cur.barcode && ' (ตาม Barcode)'}</span>
                <Select value={cur.uom} disabled={!!cur.barcode} onChange={(e) => setCur({ ...cur, uom: e.target.value })}>
                  {cur.item.uoms.some((u) => u.uom_code === cur.uom) ? null : <option value={cur.uom}>{cur.uom} (แปลงหน่วยไม่ได้)</option>}
                  <UomOptions item={cur.item} />
                </Select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm text-muted">จำนวน</span>
                <Input
                  ref={qtyRef}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void save();
                    } else if (e.key === 'Escape') clear();
                  }}
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="0"
                  className="text-lg"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={clear}>ยกเลิก</Button>
              <Button variant="primary" size="lg" className="grow" onClick={() => void save()}>
                บันทึก
              </Button>
            </div>
          </div>
        )}
        {msg && (
          <div className="mt-3">
            <Alert tone={msg.tone}>{msg.text}</Alert>
          </div>
        )}
      </Card>

      <Card title="รายการล่าสุดของฉัน" className="mt-4">
        {recentRows.length === 0 ? (
          <p className="text-sm text-muted">ยังไม่มีรายการ</p>
        ) : (
          <ul className="divide-y divide-line">
            {recentRows.map((r) => {
              const gone = r.status === 'VOID' || r.status === 'CANCELLED' || r.status === 'VOIDING';
              return (
                <li key={r.id} className="flex items-start gap-2 py-2 text-sm">
                  <span className="w-11 shrink-0 text-muted">{hm(r.at)}</span>
                  <span className={`min-w-0 grow ${gone ? 'text-muted line-through' : ''}`}>
                    {r.item_code} {r.item_name}
                    <span className={`block ${gone ? '' : 'font-medium'}`}>
                      {fmtQty(r.qty)} {r.uom}
                    </span>
                    {r.note && (gone || r.status === 'REJECTED') && (
                      <span className={`block text-xs no-underline ${r.status === 'REJECTED' ? 'text-bad' : 'text-muted'}`} style={{ textDecoration: 'none' }}>
                        {r.status === 'REJECTED' ? r.note : `เหตุผล: ${r.note}`}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 space-y-1 text-right">
                    <span className="block">
                      {r.status === 'PENDING' && <Badge tone="warn">รอส่ง</Badge>}
                      {r.status === 'REJECTED' && <Badge tone="bad">ถูกปฏิเสธ</Badge>}
                      {r.status === 'CANCELLED' && <Badge>ยกเลิก</Badge>}
                      {r.status === 'VOID' && <Badge>ยกเลิก</Badge>}
                      {r.status === 'VOIDING' && <Badge tone="warn">กำลังยกเลิก</Badge>}
                    </span>
                    {canVoid(r) && (
                      <span className="flex gap-1">
                        <Button size="sm" onClick={() => setVoidTarget({ row: r, mode: 'fix' })}>
                          แก้
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => setVoidTarget({ row: r, mode: 'void' })}>
                          ยกเลิก
                        </Button>
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <VoidDialog
        open={!!voidTarget}
        title={voidTarget?.mode === 'fix' ? 'แก้ยอด (ยกเลิกรายการเดิมแล้วกรอกใหม่)' : 'ยกเลิกรายการนี้ (ยอดจะถูกหักออก)'}
        confirmLabel={voidTarget?.mode === 'fix' ? 'ยกเลิกรายการเดิม แล้วกรอกจำนวนใหม่' : 'ยืนยันยกเลิกรายการ'}
        placeholder={voidTarget?.mode === 'fix' ? 'เช่น คีย์จำนวนผิด — ยอดใหม่กรอกในขั้นถัดไป' : 'เช่น คีย์ผิด, นับซ้ำ — ไม่ต้องใส่ยอดใหม่ที่นี่'}
        hint={voidTarget?.mode === 'fix' ? undefined : 'ถ้าต้องการแก้ยอด ให้กดปุ่ม แก้ แทน'}
        onSwitchToFix={voidTarget?.mode === 'void' ? () => setVoidTarget({ row: voidTarget.row, mode: 'fix' }) : undefined}
        summary={voidTarget ? `${voidTarget.row.item_code} ${voidTarget.row.item_name} · ${fmtQty(voidTarget.row.qty)} ${voidTarget.row.uom} · ${hm(voidTarget.row.at)}` : undefined}
        onClose={() => {
          setVoidTarget(null);
          focusScan();
        }}
        onConfirm={doVoid}
      />

      <Modal open={dialog?.type === 'search'} title="ค้นหาสินค้า" onClose={closeDialog}>
        <ItemPicker
          onPick={async (i) => {
            const unbound = dialog?.type === 'search' ? (dialog.unbound ?? null) : null;
            setDialog(null);
            const item = await getItem(i.item_code).catch(() => null);
            if (item) select({ item, uom: item.inventory_uom, barcode: null, method: 'SEARCH', unbound, replaces: null });
          }}
        />
      </Modal>

      <Modal open={dialog?.type === 'bind'} title={dialog?.type === 'bind' && dialog.barcode ? 'Barcode ใหม่ — เลือกสินค้าเพื่อผูก' : 'บันทึก Barcode'} onClose={closeDialog}>
        {dialog?.type === 'bind' && (
          <>
            {dialog.barcode && <p className="mb-3 text-sm text-muted">ไม่พบ Barcode นี้ในระบบ ผูกกับสินค้าแล้วนับต่อได้ทันที — ตรวจชื่อสินค้าและหน่วยให้ถูกต้อง</p>}
            <BarcodeBindForm
              barcode={dialog.barcode}
              item={dialog.item}
              uom={dialog.uom}
              sessionId={id}
              onCancel={closeDialog}
              onDone={async (b, item) => {
                setDialog(null);
                await rememberBarcode(b); // usable offline immediately
                const fresh = (await getItem(item.item_code)) ?? item;
                feedback(true);
                if (dialog.barcode) {
                  setMsg({ tone: 'ok', text: `ผูก Barcode ${b.barcode} → ${b.item_code} (${b.uom_code}) แล้ว` });
                  select({ item: fresh, uom: b.uom_code, barcode: b.barcode, method: 'SCAN', unbound: null, replaces: null });
                } else if (cur) {
                  setMsg({ tone: 'ok', text: `บันทึก Barcode ${b.barcode} ให้ ${b.item_code} (${b.uom_code}) แล้ว` });
                  setCur({ ...cur, item: fresh });
                  setTimeout(() => qtyRef.current?.focus(), 30);
                }
              }}
            />
          </>
        )}
      </Modal>

      <Modal open={dialog?.type === 'offline'} title="ไม่ได้เชื่อมต่อเน็ต" onClose={closeDialog}>
        {dialog?.type === 'offline' && (
          <div className="space-y-3">
            <p>
              ตรวจ Barcode <b className="font-mono">{dialog.barcode}</b> ไม่ได้ เพราะไม่ได้เชื่อมต่อเน็ต (การผูก Barcode ใหม่ต้อง Online)
            </p>
            <Button variant="primary" className="w-full" onClick={() => setDialog({ type: 'search', unbound: dialog.barcode })}>
              ค้นหาสินค้าแล้วนับโดยไม่ผูก Barcode
            </Button>
            <Button className="w-full" onClick={closeDialog}>
              ข้ามไปก่อน
            </Button>
          </div>
        )}
      </Modal>

      <Modal open={dialog?.type === 'camera'} title="สแกนด้วยกล้อง" onClose={closeDialog}>
        {dialog?.type === 'camera' && (
          <CameraScanner
            onCode={(code) => {
              setDialog(null);
              void handleScan(code);
            }}
          />
        )}
      </Modal>
    </div>
  );
}
